'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Typography,
} from '@mui/material';
import { usePrivy, useWallets } from '@privy-io/react-auth';

import {
  useAuth,
  usePolymarketClient,
  useProxyInitialization,
  useT,
  useTokenApprovals,
} from '@/hooks';
import useClobCredentials from '@/hooks/useClobCredentials';
import { useAppDispatch } from '@/store';
import { setSetupRequested } from '@/store/slices/auth.slice';

/**
 * ProxyWalletInitializer
 *
 * Invisible background component that silently orchestrates the full
 * proxy wallet setup flow whenever the user logs in or refreshes.
 * Renders nothing — no modals, no progress UI.
 */
export function ProxyWalletInitializer() {
  const { t } = useT();
  const dispatch = useAppDispatch();
  const { logout } = usePrivy();
  const { user, isAuthenticated, isProxyInitializing, isSetupRequested, isReadyToTrade } =
    useAuth();
  const { wallets } = useWallets();
  const { client, isInitializing: isClientInitializing } = usePolymarketClient();
  const { initializeProxy } = useProxyInitialization();
  const { isProvisioning: isClobProvisioning } = useClobCredentials();
  const { checkAllTokenApprovals, isCheckingApprovals, isSettingApprovals } = useTokenApprovals();

  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [approvalRequested, setApprovalRequested] = useState(false);
  const [approvalError, setApprovalError] = useState<string | null>(null);

  const isExecutingRef = useRef(false);
  // If embedded wallet is unavailable (external-wallet session), fall back to any connected wallet.
  const linkedWallet = wallets.find(
    (wallet) => wallet?.address?.toLowerCase() === user?.walletAddress?.toLowerCase()
  );
  const embeddedWallet = wallets.find((wallet) => wallet.walletClientType === 'privy');
  const activeWallet = linkedWallet || embeddedWallet || wallets[0];
  const activeWalletAddress = activeWallet?.address;
  const activeWalletType = activeWallet?.walletClientType === 'privy' ? 'privy' : 'external';

  const resetFlow = useCallback(async () => {
    setApprovalModalOpen(false);
    setApprovalRequested(false);
    setApprovalError(null);
    dispatch(setSetupRequested(false));
    try {
      await logout();
    } catch (err) {
      console.error(
        '[ProxyWalletInitializer] Logout failed after approval flow cancellation:',
        err
      );
    }
  }, [dispatch, logout]);

  useEffect(() => {
    const readyToInitialize =
      isAuthenticated &&
      isSetupRequested &&
      !isReadyToTrade &&
      !!user?.walletAddress &&
      !!activeWalletAddress &&
      !!client &&
      !isProxyInitializing &&
      !isClientInitializing &&
      !isClobProvisioning &&
      !isSettingApprovals &&
      !isExecutingRef.current;

    if (!readyToInitialize) {
      return;
    }

    if (
      activeWalletType === 'external' &&
      !approvalRequested &&
      !approvalModalOpen &&
      !isCheckingApprovals
    ) {
      const checkApprovals = async () => {
        setApprovalError(null);

        if (user?.proxyWalletAddress) {
          try {
            const status = await checkAllTokenApprovals(user.proxyWalletAddress);
            if (status.allApproved) {
              setApprovalRequested(true);
              return;
            }
          } catch (err) {
            console.warn('[ProxyWalletInitializer] Approval check failed:', err);
            setApprovalError(
              t('proxyWallet.approvalCheckFailed', {
                defaultValue:
                  'Unable to verify contract approvals. Please approve from your wallet when prompted.',
              })
            );
          }
        }

        setApprovalModalOpen(true);
      };

      void checkApprovals();
      return;
    }

    if (activeWalletType !== 'external' || approvalRequested) {
      isExecutingRef.current = true;

      initializeProxy(client)
        .catch(async (err) => {
          console.error('[ProxyWalletInitializer] Proxy initialization failed:', err);
          if (activeWalletType === 'external') {
            await resetFlow();
          }
        })
        .finally(() => {
          isExecutingRef.current = false;
          dispatch(setSetupRequested(false));
        });
    }
  }, [
    activeWalletType,
    activeWalletAddress,
    approvalRequested,
    approvalModalOpen,
    client,
    dispatch,
    initializeProxy,
    isAuthenticated,
    isClobProvisioning,
    isCheckingApprovals,
    isClientInitializing,
    isProxyInitializing,
    isReadyToTrade,
    isSettingApprovals,
    isSetupRequested,
    resetFlow,
    user?.proxyWalletAddress,
    user?.walletAddress,
    checkAllTokenApprovals,
  ]);

  const handleApprove = async () => {
    setApprovalRequested(true);
    setApprovalModalOpen(false);
    setApprovalError(null);
  };

  return (
    <Dialog
      open={approvalModalOpen}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      }}
      disableEscapeKeyDown
      aria-labelledby="external-wallet-approval-dialog-title"
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 4,
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: 24,
          px: 0,
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ width: '100%', height: 6, bgcolor: 'primary.main' }} />
      <DialogContent sx={{ pt: 4, pb: 3, px: 3, textAlign: 'center' }}>
        <Box
          sx={{
            mx: 'auto',
            mb: 2.5,
            width: 60,
            height: 60,
            borderRadius: '50%',
            bgcolor: 'action.selected',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 4,
          }}
        >
          <AccountBalanceWalletOutlinedIcon fontSize="large" />
        </Box>

        <Typography
          variant="h6"
          component="div"
          sx={{ mb: 1.5, fontWeight: 800, letterSpacing: 0.14 }}
        >
          {t('proxyWallet.approvalRequired', { defaultValue: 'External wallet approval required' })}
        </Typography>

        <DialogContentText color="text.primary" sx={{ mb: 2, typography: 'body2', opacity: 0.92 }}>
          {t('proxyWallet.approvalDesc', {
            defaultValue:
              'You are connected with an external wallet. To trade on Porvenix, approve the trading contract from your wallet.',
          })}
        </DialogContentText>
        <DialogContentText
          color="text.secondary"
          sx={{ mb: 2, typography: 'body2', lineHeight: 1.75 }}
        >
          {t('proxyWallet.approvalInstructions', {
            defaultValue:
              'Tap Approve from wallet to open your external wallet and confirm the approval request. Once confirmed, the setup will continue automatically.',
          })}
        </DialogContentText>
        <DialogContentText
          color="text.secondary"
          sx={{ mb: 2, typography: 'body2', lineHeight: 1.75 }}
        >
          {t('proxyWallet.approvalFailureNotice', {
            defaultValue:
              'If the approval fails or is rejected, the setup flow will restart automatically.',
          })}
        </DialogContentText>
        {approvalError ? (
          <DialogContentText color="error" sx={{ mt: 1, typography: 'body2', fontWeight: 600 }}>
            {approvalError}
          </DialogContentText>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          fullWidth
          size="large"
          onClick={handleApprove}
          disabled={approvalRequested}
          sx={{
            py: 1.5,
            textTransform: 'none',
            borderRadius: 3,
            fontWeight: 700,
            color: '#fff',
            background: 'linear-gradient(135deg, #4F7DFF 0%, #1C5DFF 100%)',
            boxShadow: '0 16px 38px rgba(31, 95, 255, 0.18)',
            '&:hover': {
              background: 'linear-gradient(135deg, #3d6bff 0%, #184cd9 100%)',
            },
          }}
        >
          {t('proxyWallet.approveBtn', { defaultValue: 'Approve from wallet' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ProxyWalletInitializer;
